import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { createSkill, getSkills, updateSkill } from '../services/skill.service';
import { GetSkillsParams, SkillInsertInput, SkillUpdateInput } from '../types/skill.types';

/** The manager list and the profile's skill lookup (`hr-skill-taxonomy`) both show skills. */
const invalidateSkillQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['hr-skills'] }),
    queryClient.invalidateQueries({ queryKey: ['hr-skill-taxonomy'] }),
  ]);

export const useGetSkills = (params: GetSkillsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-skills', params],
    queryFn: () => getSkills(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateSkill = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: SkillInsertInput) => createSkill(input),
    onSuccess: () => {
      invalidateSkillQueries(queryClient);
      toast({ title: t('SKILL_CREATED') });
    },
  });
};

export const useUpdateSkill = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: SkillUpdateInput }) => updateSkill(params),
    onSuccess: () => {
      invalidateSkillQueries(queryClient);
      toast({ title: t('SKILL_UPDATED') });
    },
  });
};
